import { useCallback } from 'react'
import { dispatch } from 'store'
import { aiAvatarThunks } from 'store/features/aiAvatar/thunks'
import { AvatarSource } from 'store/types/aiAvatar'
import { IMyFilesMediaItem, IUploadMediaItem } from 'types/media'
import { useFileFormatValidator } from 'hooks/useFileFormatValidator'
import { useUploadStorageGuard } from 'hooks/useUploadStorageGuard'

export const useAvatarUpload = () => {
  const validateFiles = useFileFormatValidator('image')
  const ensureStorageForUpload = useUploadStorageGuard()

  const handleFilesUpload = useCallback(
    async (files: File[] | FileList) => {
      if (!files || !files.length) return
      const valid = validateFiles(Array.from(files))
      if (!valid.length) return

      // Only the first file is uploaded — gate on exactly what we will send.
      // Aborting here (library full) keeps the popup consistent with every
      // other upload path and never puts the avatar into a loading state.
      const [file] = valid
      if (!(await ensureStorageForUpload([file]))) return

      const uploadItem: IUploadMediaItem = {
        id: crypto.randomUUID(),
        type: 'image',
        file
      }
      dispatch(aiAvatarThunks.uploadAvatar([uploadItem]))
    },
    [validateFiles, ensureStorageForUpload]
  )

  const handleMediaLibFiles = useCallback((files: IMyFilesMediaItem[]) => {
    const file = files?.[0]
    if (!file) return

    dispatch(
      aiAvatarThunks.setAvatar({
        id: String(file.id),
        url: file.filePath,
        source: AvatarSource.MY_FILES,
        width: file.width,
        height: file.height
      })
    )
  }, [])

  const handleRemoveAvatar = useCallback(() => {
    dispatch(aiAvatarThunks.clearAvatar())
  }, [])

  const handleSelectPublicAvatar = useCallback(
    (id: string, url: string, gender: string, aspectRatio: string) => {
      dispatch(
        aiAvatarThunks.setAvatar({
          id,
          url,
          source: AvatarSource.PUBLIC
        })
      )
      // Commit the panel's chosen aspect ratio alongside the avatar — Redux holds
      // a ratio only while a public avatar is selected (cleared on remove).
      dispatch(aiAvatarThunks.setAvatarAspectRatio(aspectRatio))
      // Auto-pick a voice matching the avatar's gender from the current language.
      dispatch(aiAvatarThunks.selectSpeakerByGender(gender))
    },
    []
  )

  return {
    handleFilesUpload,
    handleMediaLibFiles,
    handleRemoveAvatar,
    handleSelectPublicAvatar
  }
}
